import { useCan } from '@/components/can/Can';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import PointsConfiguration from '@/features/points-management/points-configuration';
import { useCustomerPointSettingList } from '@/features/points-management/points-configuration/customerTable/hooks/useCustomerPointSettingList';
import {
  useAffiliateCategory,
  useClubCategory,
} from '@/features/points-management/points-configuration/hooks/usePointsConfiguration';
import { createFileRoute, SearchSchemaInput, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

const sortItemSchema = z.object({
  id: z.enum(['categoryName', 'categoryType', 'description', 'createdDate', 'status', 'createdBy', 'category']),
  desc: z.boolean(),
});

const pointConfigurationSearchSchema = z
  .object({
    sort: z
      .preprocess((val) => {
        if (typeof val === 'string') {
          try {
            return JSON.parse(val);
          } catch {
            return undefined;
          }
        }
        return val;
      }, z.array(sortItemSchema))
      .optional(),

    createdDate: z.string().optional(),

    q: z.string().optional(),

    status: z
      .string()
      .transform((val) => val.split(','))
      .pipe(z.array(z.enum(['ACTIVE', 'BLOCKED'])))
      .optional(),

    page: z.coerce.number().nonnegative().catch(0),
    fetch: z.coerce.number().positive().max(100).catch(10),
    activeTab: z.enum(['club', 'affiliate', 'customer']).optional(),
  })
  .passthrough();
export const Route = createFileRoute('/(app)/points-management/points-configuration/')({
  component: RouteComponent,
  validateSearch: (search: SearchSchemaInput) => pointConfigurationSearchSchema.parse(search),
});

function RouteComponent() {
  const { activeTab: activeTabSearch } = useSearch({
    from: '/(app)/points-management/points-configuration/',
  });

  const canAccessAffiliatePoint = useCan({ allOf: [PERMS.point.management.list] });
  const canAccessCustomerPoint = useCan({ allOf: [PERMS.point.management.customer.list] });

  const defaultTab = canAccessAffiliatePoint ? 'affiliate' : canAccessCustomerPoint ? 'customer' : 'affiliate';
  const activeTab = activeTabSearch ?? defaultTab;

  const affiliate = useAffiliateCategory(activeTab === 'affiliate');
  const club = useClubCategory(activeTab === 'club');
  const customer = useCustomerPointSettingList();

  const dataMap = {
    affiliate,
    club,
    customer,
  };
  const result = dataMap[activeTab];
  return <PointsConfiguration data={result.categories} isLoading={result.isLoading} pagination={result.meta} />;
}
